// src/components/StatsPage.js

import React, { useState, useEffect } from 'react';
import './StatsPage.css';

const StatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
	  const response = await fetch('./api/stats-bn2025xyz')
      
      if (!response.ok) {
        throw new Error('Erreur lors du chargement des statistiques');
      }

      const data = await response.json();
      
      if (data.success) {
        setStats(data.data);
        setLastUpdated(new Date().toLocaleString('fr-FR', {
          timeZone: 'Africa/Douala',
          dateStyle: 'short',
          timeStyle: 'medium'
        }));
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      setError(err.message);
      console.error('Erreur fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading && !stats) {
    return (
      <div className="stats-page">
        <div className="stats-loading">
          <div className="guinness-loader"></div>
          <p>Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="stats-page">
        <div className="stats-error">
          <h2>❌ Erreur</h2>
          <p>{error}</p>
          <button onClick={fetchStats} className="btn-refresh">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="stats-page">
      {/* Header */}
      <header className="stats-header">
        <div className="header-content">
          <h1>📊 Guinness Bright Night - Dashboard</h1>
          <p className="event-name">{stats.event?.name || 'Bright Night 2025'}</p>
          <div className="header-actions">
            <span className="last-update">
              Dernière mise à jour : {lastUpdated}
            </span>
            <button 
              onClick={fetchStats} 
              className="btn-refresh"
              disabled={loading}
            >
              {loading ? '⏳' : '🔄'} Rafraîchir
            </button>
          </div>
        </div>
      </header>

      <div className="stats-container">
        
        {/* Section 1: Vue d'ensemble - Cartes principales */}
        <section className="stats-section">
          <h2 className="section-title">📈 Vue d'Ensemble</h2>
          <div className="cards-grid">
            <div className="stat-card card-primary">
              <div className="card-icon">🎫</div>
              <div className="card-content">
                <h3>Total Tickets</h3>
                <p className="card-value">{stats.tickets.total.toLocaleString('fr-FR')}</p>
                <span className="card-label">tickets générés</span>
              </div>
            </div>

            <div className="stat-card card-success">
              <div className="card-icon">💰</div>
              <div className="card-content">
                <h3>Total Encaissé</h3>
                <p className="card-value">{stats.payments.total_encaisse.toLocaleString('fr-FR')} FCFA</p>
                <span className="card-label">{stats.payments.total_paiements} paiements réussis</span>
              </div>
            </div>

            <div className="stat-card card-info">
              <div className="card-icon">✅</div>
              <div className="card-content">
                <h3>Validations Entrée</h3>
                <p className="card-value">{stats.scans.total.toLocaleString('fr-FR')}</p>
                <span className="card-label">tickets scannés</span>
              </div>
            </div>

            <div className="stat-card card-warning">
              <div className="card-icon">👥</div>
              <div className="card-content">
                <h3>Présence Actuelle</h3>
                <p className="card-value">{stats.current_attendance.current.toLocaleString('fr-FR')}</p>
                <span className="card-label">entrées uniques</span>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Tickets par type */}
        <section className="stats-section">
          <h2 className="section-title">🎟️ Répartition des Tickets</h2>
          <div className="tickets-grid">
            {stats.tickets.by_type.map((ticket) => (
              <div key={ticket.type} className={`ticket-card ticket-${ticket.type}`}>
                <div className="ticket-header">
                  <h3>{ticket.label}</h3>
                  <span className="ticket-badge">{ticket.type}</span>
                </div>
                <div className="ticket-stats">
                  <div className="ticket-stat">
                    <span className="stat-label">Nombre</span>
                    <span className="stat-value">{ticket.total.toLocaleString('fr-FR')}</span>
                  </div>
                  <div className="ticket-stat">
                    <span className="stat-label">Montant payé</span>
                    <span className="stat-value">{ticket.total_amount.toLocaleString('fr-FR')} FCFA</span>
                  </div>
                  <div className="ticket-stat">
                    <span className="stat-label">Bons d'achat</span>
                    <span className="stat-value">{ticket.total_vouchers.toLocaleString('fr-FR')} FCFA</span>
                  </div>
                </div>
                <div className="ticket-percentage">
                  {((ticket.total / stats.tickets.total) * 100).toFixed(1)}% du total
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3: Stock VIP */}
        <section className="stats-section">
          <h2 className="section-title">👑 Stock VIP</h2>
          <div className="vip-stock">
            <div className="vip-progress">
              <div className="vip-bar-container">
                <div 
                  className="vip-bar-fill" 
                  style={{ width: `${(stats.vip.current / stats.vip.max) * 100}%` }}
                ></div>
              </div>
              <div className="vip-numbers">
                <span>{stats.vip.current} / {stats.vip.max} VIP vendus</span>
                <span className="vip-remaining">
                  {stats.vip.remaining} {stats.vip.remaining > 1 ? 'places restantes' : 'place restante'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Statistiques Paiements */}
        <section className="stats-section">
          <h2 className="section-title">💳 Statistiques Paiements</h2>
          
          <div className="payment-stats-grid">
            {/* Répartition par méthode */}
            <div className="payment-card">
              <h3>Répartition par Méthode</h3>
              <div className="payment-methods">
                {stats.payments.by_method.map((method) => (
                  <div key={method.method} className="method-item">
                    <div className="method-header">
                      <span className={`method-icon ${method.method.toLowerCase()}`}>
                        {method.method === 'mtn' ? '📱' : '🍊'}
                      </span>
                      <span className="method-name">
                        {method.method === 'mtn' ? 'MTN Mobile Money' : 'Orange Money'}
                      </span>
                    </div>
                    <div className="method-stats">
                      <div>{method.total} paiements</div>
                      <div className="method-amount">{parseFloat(method.total_amount).toLocaleString('fr-FR')} FCFA</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Répartition par statut */}
            <div className="payment-card">
              <h3>Répartition par Statut</h3>
              <div className="payment-statuses">
                {stats.payments.by_status.map((status) => (
                  <div key={status.status} className={`status-item status-${status.status}`}>
                    <span className="status-label">{status.status.toUpperCase()}</span>
                    <span className="status-count">{status.total}</span>
                    <span className="status-amount">
                      {parseFloat(status.total_amount || 0).toLocaleString('fr-FR')} FCFA
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Taux de conversion */}
          <div className="conversion-card">
            <h3>📊 Taux de Conversion Paiement → Inscription</h3>
            <div className="conversion-stats">
              <div className="conversion-item">
                <span className="conversion-label">Tentatives</span>
                <span className="conversion-value">{stats.conversion.total_payment_attempts}</span>
              </div>
              <div className="conversion-arrow">→</div>
              <div className="conversion-item">
                <span className="conversion-label">Réussies</span>
                <span className="conversion-value">{stats.conversion.total_payment_success}</span>
              </div>
              <div className="conversion-arrow">→</div>
              <div className="conversion-item">
                <span className="conversion-label">Tickets</span>
                <span className="conversion-value">{stats.conversion.total_paid_tickets}</span>
              </div>
            </div>
            <div className="conversion-rate">
              Taux de réussite : <strong>{stats.conversion.conversion_rate}%</strong>
            </div>
          </div>
        </section>

        {/* Section 5: Classement Créateurs */}
        <section className="stats-section">
          <h2 className="section-title">🎨 Classement des Créateurs de Contenu</h2>
          
          {/* Stats par catégorie */}
          <div className="categories-grid">
            {stats.creators.by_category.map((category, index) => (
              <div key={index} className="category-card">
                <h3 className="category-name">{category.category}</h3>
                <div className="category-total">
                  {category.total_invitations} {category.total_invitations > 1 ? 'invitations' : 'invitation'}
                </div>
                <div className="category-creators">
                  {category.creators.map((creator) => (
                    <div key={creator.id} className="creator-mini">
                      <span className="creator-name">{creator.name}</span>
                      <span className="creator-count">{creator.total_invitations}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Tableau classement global */}
          <div className="creators-table-container">
            <table className="creators-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Créateur</th>
                  <th>Catégorie</th>
                  <th>Code</th>
                  <th>Invitations</th>
                  <th>Contact</th>
                </tr>
              </thead>
              <tbody>
                {stats.creators.all_creators.map((creator, index) => (
                  <tr key={creator.id} className={index < 3 ? 'top-creator' : ''}>
                    <td className="rank">
                      {index === 0 && '🥇'}
                      {index === 1 && '🥈'}
                      {index === 2 && '🥉'}
                      {index > 2 && (index + 1)}
                    </td>
                    <td className="creator-name-col">{creator.name}</td>
                    <td>
                      <span className={`category-badge category-${creator.category.toLowerCase().replace(' ', '-')}`}>
                        {creator.category}
                      </span>
                    </td>
                    <td className="ref-code">{creator.ref_code}</td>
                    <td className="invitations-count">{creator.total_invitations}</td>
                    <td className="contact">
                      {creator.phone && <div>📞 {creator.phone}</div>}
                      {creator.email && <div>✉️ {creator.email}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 6: Scans récents */}
        <section className="stats-section">
          <h2 className="section-title">🔍 Derniers Scans d'Entrée</h2>
          
          {/* Stats par type de ticket scanné */}
          <div className="scans-summary">
            {stats.scans.by_type.map((scan) => (
              <div key={scan.ticket_type} className="scan-type-item">
                <span className="scan-type-label">{scan.ticket_type.toUpperCase()}</span>
                <span className="scan-type-count">{scan.total_scans} scans</span>
              </div>
            ))}
          </div>

          {/* Tableau des derniers scans */}
          <div className="recent-scans-table">
            <table>
              <thead>
                <tr>
                  <th>Heure</th>
                  <th>Nom</th>
                  <th>Code Ticket</th>
                  <th>Type</th>
                  <th>Appareil</th>
                </tr>
              </thead>
              <tbody>
                {stats.scans.recent_scans.map((scan, index) => (
                  <tr key={index}>
                    <td>{new Date(scan.scan_time).toLocaleString('fr-FR', { 
                      timeZone: 'Africa/Douala',
                      dateStyle: 'short',
                      timeStyle: 'short'
                    })}</td>
                    <td>{scan.first_name} {scan.last_name}</td>
                    <td className="ticket-code">{scan.ticket_code}</td>
                    <td>
                      <span className={`badge badge-${scan.ticket_type}`}>
                        {scan.ticket_type}
                      </span>
                    </td>
                    <td className="device-id">{scan.device_id || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 7: Timeline */}
        <section className="stats-section">
          <h2 className="section-title">📅 Évolution des Inscriptions</h2>
          
          <div className="timeline-container">
            <h3>Par Jour</h3>
            <div className="timeline-chart">
              {stats.timeline.by_day.length > 0 ? (
                <table className="timeline-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Nombre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.timeline.by_day.map((day, index) => (
                      <tr key={index}>
                        <td>{new Date(day.date).toLocaleDateString('fr-FR')}</td>
                        <td>
                          <span className={`badge badge-${day.ticket_type}`}>
                            {day.ticket_type}
                          </span>
                        </td>
                        <td className="timeline-count">{day.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">Aucune donnée disponible</p>
              )}
            </div>

            {stats.timeline.today_by_hour.length > 0 && (
              <>
                <h3>Aujourd'hui (par heure)</h3>
                <div className="timeline-chart">
                  <table className="timeline-table">
                    <thead>
                      <tr>
                        <th>Heure</th>
                        <th>Type</th>
                        <th>Nombre</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.timeline.today_by_hour.map((hour, index) => (
                        <tr key={index}>
                          <td>{hour.hour}h</td>
                          <td>
                            <span className={`badge badge-${hour.ticket_type}`}>
                              {hour.ticket_type}
                            </span>
                          </td>
                          <td className="timeline-count">{hour.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </section>

      </div>

      {/* Footer */}
      <footer className="stats-footer">
        <p>Guinness Bright Night 2025 - Dashboard de Statistiques</p>
        <p className="footer-timestamp">Généré le {lastUpdated}</p>
      </footer>
    </div>
  );
};

export default StatsPage;